import { Controller } from "stimulus"
import SlimSelect from "slim-select"
import { xhr, translator, createElement } from "../utils"

const t = translator({
  de: {
    placeholderText: null,
    searchText: "Nichts gefunden",
    searchPlaceholder: "Suche",
    searchMinLength: "Mindestens {number} Zeichen erforderlich",
    searchingText: "Suche..."
  },
  en: {
    searchMinLength: "Search must be at least {number} characters"
  }
})

function parseOptions(el) {
  try {
    return JSON.parse(el.getAttribute("data-options")) || {}
  } catch (e) {
    return {}
  }
}

function debouncePromise(func, wait) {
  let timeout
  return (...args) => {
    clearTimeout(timeout)
    return new Promise((resolve, reject) => {
      timeout = setTimeout(() => {
        func.apply(this, args).then(resolve).catch(reject)
      }, wait || 350)
    })
  }
}

export default class extends Controller {
  static values = {
    url: String,
    minLength: Number,
    searchParam: { type: String, default: "q" },
    pageParam: { type: String, default: "page" },
    loadingText: String
  }

  initialize() {
    this.#configurePlaceholder()
    this.element.parentNode.classList.remove("select")
  }

  connect() {
    const options = this.#makeOptions()
    this.slimSelect = new SlimSelect(options)

    if (this.urlValue) {
      this.#initInfiniteScrolling(this.slimSelect)
    }
  }

  disconnect() {
    this.#deinitInfiniteScrolling(this.slimSelect)
    this.slimSelect?.destroy()
  }

  #loadMoreOptions() {
    if (this._loadingMore || !this.nextPageUrl) return
    this._loadingMore = true

    const progressEl = this.#addProgressOption()
    this.#fetch(this.nextPageUrl)
      .then(this.#appendData)
      .finally(() => {
        this._loadingMore = false
        progressEl.remove()
      })
  }

  #makeOptions() {
    const options = parseOptions(this.element)
    const overrides = {
      select: this.element,
      settings: this.settings,
      events: {
        search: this.#makeSearchHandler()
      }
    }

    return Object.assign(options, overrides)
  }

  get settings() {
    return {
      placeholderText: this.element.getAttribute("placeholder") || this.element.dataset.placeholder,
      searchText: this.element.getAttribute("data-search-text") || t("searchText"),
      searchPlaceholder: this.element.getAttribute("data-search-placeholder") || t("searchPlaceholder"),
      searchingText: this.element.dataset.searchingText || t("searchingText"),
      searchHighlight: true
    }
  }

  #makeSearchHandler() {
    if (this.urlValue) {
      return debouncePromise(this.#searchHandler).bind(this)
    }
  }

  #searchHandler = search => {
    return new Promise((resolve, reject) => {
      this.currentPage = 1
      this.totalPages = 2
      this.nextPageUrl = this.urlValue

      if (search.length == 0) {
        resolve(this.slimSelect.getData())
        return
      }

      if (search.length < this.minLengthValue) {
        reject(t("searchMinLength", { number: this.minLengthValue }))
        return
      }

      this.#fetch(this.#makeSearchUrl(search)).then(resolve)
    })
  }

  #fetch(url) {
    return xhr(url, { headers: { Accept: 'application/json' } })
      .then(this.#parsePageHeaders)
      .then(response => response.json())
  }

  #parsePageHeaders = response => {
    this.currentPage = parseInt(response.headers.get("current-page"), 10)
    this.totalPages = parseInt(response.headers.get("total-pages"), 10)

    const links = response.headers.get("Link") || ""
    const [ , url ] = links.match(/<([^>]+)>; rel="next"/) || []
    this.nextPageUrl = url

    return response
  }

  #makeSearchUrl(q) {
    const url = new URL(this.urlValue)
    const params = new URLSearchParams(url.search)
    params.set(this.searchParamValue, q)
    params.set(this.pageParamValue, this.currentPage)
    if (!q) params.delete(this.searchParamValue)
    url.search = params.toString()

    return url
  }

  #appendData = data => {
    if (data.length) {
      this.slimSelect.setData(this.slimSelect.getData().concat(data))
    }
  }

  #initInfiniteScrolling(slim) {
    this.currentPage = 1
    this.totalPages = 2
    this.nextPageUrl = this.urlValue

    slim.render.content.list.addEventListener(
      'scroll',
      this.scrollHandler,
      { passive: true }
    )
  }

  #deinitInfiniteScrolling(slim) {
    slim.render.content.list.removeEventListener('scroll', this.scrollHandler)
  }

  scrollHandler = () => {
    const {
      scrollTop,
      scrollHeight,
      clientHeight
    } = this.slimSelect.render.content.list

    const hasMorePages = this.currentPage < this.totalPages
    const scrollThresholdReached = scrollTop + clientHeight >= scrollHeight - 50

    if (scrollThresholdReached && hasMorePages) {
      this.#loadMoreOptions()
    }
  }

  #addProgressOption() {
    if (!this._progressOptionElement) {
      const progress = createElement("div", { class: "ss-loader" })
      progress.append(createElement("span", { class: "loader" }))
      progress.append(createElement("p", { innerHTML: this.loadingTextValue }))
      this._progressOptionElement = progress
    }

    this.slimSelect.render.content.list.append(this._progressOptionElement)

    return this._progressOptionElement
  }

  #configurePlaceholder() {
    const firstOption = this.element.querySelector("option:first-child")
    if (firstOption && firstOption.value == "") {
      if (!this.element.multiple) {
        firstOption.after(firstOption.cloneNode(true))
      }
      firstOption.setAttribute("data-placeholder", true)
    }
  }
}
